import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { Unit, UnitInsert, UnitUpdate, UnitWithResidence } from '../types'

const UNITS_QUERY_KEY = ['units']

export interface UnitsFilters {
  residenceId?: string
  ownerName?: string
  page?: number
  pageSize?: number
  sortField?: string
  sortDirection?: 'asc' | 'desc'
}

export interface UnitsPaginatedResponse {
  data: UnitWithResidence[]
  count: number
  page: number
  pageSize: number
  totalPages: number
}

/**
 * Fetch units with server-side pagination, filtering, and sorting
 * Optimized for large datasets (10000+ units)
 */
export function useUnitsPaginated(filters: UnitsFilters = {}) {
  const {
    residenceId,
    ownerName,
    page = 1,
    pageSize = 20,
    sortField = 'unit_number',
    sortDirection = 'asc',
  } = filters

  return useQuery({
    queryKey: [...UNITS_QUERY_KEY, 'paginated', filters],
    queryFn: async (): Promise<UnitsPaginatedResponse> => {
      // Ensure auth session is loaded before fetching units
      const { data: { session } } = await supabase.auth.getSession()

      if (!session) {
        // If no session, return empty (user not authenticated)
        return {
          data: [],
          count: 0,
          page,
          pageSize,
          totalPages: 0,
        }
      }

      // Start building the query
      // Use LEFT JOIN to include units without owners (vacant units)
      let query = supabase
        .from('units')
        .select(
          '*, residences(id, name, residence_key), ownerships(id, share_numerator, share_denominator, ownership_type, end_date, owners(id, first_name, last_name, email))',
          { count: 'exact' }
        )
        .is('deleted_at', null)
      // NOTE: We don't filter on ownerships.end_date here because it would exclude units without ownerships
      // We'll filter active ownerships (end_date IS NULL) after fetching the data

      // Apply filters
      if (residenceId) {
        query = query.eq('residence_id', residenceId)
      }

      // Owner name filter - using ilike for case-insensitive search
      if (ownerName && ownerName.trim()) {
        // Note: This requires a Postgres function or view for optimal performance
        // For now, we filter client-side as a fallback
        // TODO: Create a computed column or view for owner_name in database
      }

      // Apply sorting based on user selection
      if (sortField === 'residence') {
        query = query.order('residence_id', { ascending: sortDirection === 'asc' })
      } else {
        query = query.order(sortField, { ascending: sortDirection === 'asc' })
      }

      // Apply pagination using range
      const from = (page - 1) * pageSize
      const to = from + pageSize - 1
      query = query.range(from, to)

      const { data, error, count } = await query

      if (error) throw error

      // Filter to keep only active ownerships (end_date IS NULL)
      // This allows us to include units without any ownerships (vacant units)
      let filteredData = (data as UnitWithResidence[]).map((unit) => ({
        ...unit,
        ownerships: unit.ownerships?.filter((ownership) => ownership.end_date === null) || [],
      }))

      // Client-side owner name filtering (temporary until DB optimization)
      if (ownerName && ownerName.trim()) {
        const searchTerm = ownerName.toLowerCase().trim()
        filteredData = filteredData.filter((unit) => {
          if (!unit.ownerships || unit.ownerships.length === 0) return false
          return unit.ownerships.some((ownership) => {
            if (!ownership.owners) return false
            const fullName = `${ownership.owners.first_name} ${ownership.owners.last_name}`.toLowerCase()
            return fullName.includes(searchTerm)
          })
        })
      }

      return {
        data: filteredData,
        count: count || 0,
        page,
        pageSize,
        totalPages: Math.ceil((count || 0) / pageSize),
      }
    },
    // Always refetch on mount to ensure fresh data with current session
    staleTime: 0,
  })
}

/**
 * Fetch all units for a residence (legacy - use useUnitsPaginated for better performance)
 * @deprecated Use useUnitsPaginated instead for better performance with large datasets
 */
export function useUnits(residenceId?: string) {
  return useQuery({
    queryKey: residenceId ? [...UNITS_QUERY_KEY, 'residence', residenceId] : UNITS_QUERY_KEY,
    queryFn: async () => {
      // Ensure auth session is loaded before fetching units
      const { data: { session } } = await supabase.auth.getSession()

      if (!session) {
        // If no session, return empty (user not authenticated)
        return []
      }

      // Use LEFT JOIN to include units without owners (vacant units)
      let query = supabase
        .from('units')
        .select(
          '*, residences(id, name, residence_key), ownerships(id, share_numerator, share_denominator, ownership_type, end_date, owners(id, first_name, last_name, email))'
        )
        .is('deleted_at', null)
        .order('unit_number', { ascending: true })

      if (residenceId) {
        query = query.eq('residence_id', residenceId)
      }

      const { data, error } = await query

      if (error) throw error

      // Filter to keep only active ownerships (end_date IS NULL)
      // This allows us to include units without any ownerships (vacant units)
      const filteredData = (data as UnitWithResidence[]).map((unit) => ({
        ...unit,
        ownerships: unit.ownerships?.filter((ownership) => ownership.end_date === null) || [],
      }))

      return filteredData
    },
    // Always refetch on mount to ensure fresh data with current session
    staleTime: 0,
  })
}

/**
 * Fetch a single unit by ID with mandataire info
 */
export function useUnit(id: string | undefined) {
  return useQuery({
    queryKey: [...UNITS_QUERY_KEY, id],
    queryFn: async () => {
      if (!id) throw new Error('Unit ID is required')

      const { data, error } = await supabase
        .from('units')
        .select(`
          *,
          residences(name),
          common_rep_owner:owners!units_common_rep_owner_id_fkey(id, first_name, last_name, email, phone, notification_preference),
          common_rep_contact:external_contacts!units_common_rep_contact_id_fkey(id, first_name, last_name, email, phone, notification_preference)
        `)
        .eq('id', id)
        .is('deleted_at', null)
        .single()

      if (error) throw error
      return data
    },
    enabled: !!id,
  })
}

/**
 * Create a new unit
 */
export function useCreateUnit() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (unit: UnitInsert) => {
      const { data, error } = await supabase
        .from('units')
        .insert(unit)
        .select()
        .single()

      if (error) throw error
      return data as Unit
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: UNITS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ['properties', 'stats', data.residence_id] })
    },
  })
}

/**
 * Update an existing unit
 */
export function useUpdateUnit() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, ...updates }: UnitUpdate & { id: string }) => {
      const { data, error } = await supabase
        .from('units')
        .update(updates)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data as Unit
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: UNITS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: [...UNITS_QUERY_KEY, data.id] })
      queryClient.invalidateQueries({ queryKey: ['properties', 'stats', data.residence_id] })
    },
  })
}

/**
 * Delete a unit (soft delete)
 */
export function useDeleteUnit() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase
        .from('units')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data as Unit
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: UNITS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: ['properties', 'stats', data.residence_id] })
    },
  })
}

/**
 * Get unit owners (current ownerships)
 */
export function useUnitOwners(unitId: string | undefined) {
  return useQuery({
    queryKey: [...UNITS_QUERY_KEY, 'owners', unitId],
    queryFn: async () => {
      if (!unitId) throw new Error('Unit ID is required')

      const { data, error } = await supabase
        .from('ownerships')
        .select('*, owners(id, first_name, last_name, email, phone)')
        .eq('unit_id', unitId)
        .is('end_date', null)

      if (error) throw error
      return data
    },
    enabled: !!unitId,
  })
}

/**
 * Update unit's common representative (mandataire)
 */
export function useUpdateUnitCommonRep() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      unitId,
      repOwnerId,
      repContactId,
      designationDate,
      documentId,
    }: {
      unitId: string
      repOwnerId: string | null
      repContactId: string | null
      designationDate: string | null
      documentId?: string | null
    }) => {
      const { data, error } = await supabase
        .from('units')
        .update({
          common_rep_owner_id: repOwnerId,
          common_rep_contact_id: repContactId,
          common_rep_designation_date: designationDate,
          common_rep_document_id: documentId || null,
        })
        .eq('id', unitId)
        .select()
        .single()

      if (error) throw error
      return data as Unit
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: UNITS_QUERY_KEY })
      queryClient.invalidateQueries({ queryKey: [...UNITS_QUERY_KEY, data.id] })
      // Invalider aussi les alertes d'indivision pour le module AG
      queryClient.invalidateQueries({ queryKey: ['indivision-alerts', data.residence_id] })
    },
  })
}
